/**
 * Block category registry (guide "Catégorie & description des blocs").
 *
 * `BlocCategory` is the closed union of categories a block can belong to.
 * `BLOC_CATEGORIES` is the ordered registry consumed by the dashboard block
 * picker and the future Studio, exposing a FR label and a lucide icon name per
 * category. `category` on `DoodBlockMetadata` and `BlocDefinition` is typed
 * against this union so a typo is a compile error.
 */

export type BlocCategory =
  | 'layout'
  | 'content'
  | 'media'
  | 'actions'
  | 'navigation'
  | 'footer'
  | 'marketing'
  | 'catalog'
  | 'cart'
  | 'checkout'
  | 'loyalty'
  | 'como'
  | 'zerosix'
  | 'pongo'
  | 'kiosk'
  | 'modal'
  | 'page'
  | 'pay_at_table'
  | 'prepaid'
  | 'system';

export interface BlocCategoryEntry {
  /** Stable identifier matching the `BlocCategory` union. */
  id: BlocCategory;
  /** FR label shown in the block picker. */
  label: string;
  /** Lucide icon name, rendered by the picker. */
  icon: string;
}

/**
 * Ordered registry of block categories. The order drives the picker section
 * order; consumers must not assume alphabetical sorting.
 */
export const BLOC_CATEGORIES: readonly BlocCategoryEntry[] = [
  { id: 'layout', label: 'Mise en page', icon: 'LayoutGrid' },
  { id: 'content', label: 'Contenu', icon: 'Type' },
  { id: 'media', label: 'Média', icon: 'Image' },
  { id: 'actions', label: 'Actions', icon: 'MousePointerClick' },
  { id: 'navigation', label: 'Navigation', icon: 'Navigation' },
  { id: 'footer', label: 'Pied de page', icon: 'PanelBottom' },
  { id: 'marketing', label: 'Marketing', icon: 'Megaphone' },
  { id: 'catalog', label: 'Catalogue', icon: 'ShoppingBag' },
  { id: 'cart', label: 'Panier', icon: 'ShoppingCart' },
  { id: 'checkout', label: 'Checkout', icon: 'CreditCard' },
  { id: 'loyalty', label: 'Fidélité', icon: 'Award' },
  { id: 'como', label: 'Como', icon: 'Gem' },
  { id: 'zerosix', label: 'Zerosix', icon: 'Hexagon' },
  { id: 'pongo', label: 'Pongo', icon: 'Coins' },
  { id: 'kiosk', label: 'Borne', icon: 'Monitor' },
  { id: 'modal', label: 'Modale', icon: 'RectangleHorizontal' },
  { id: 'page', label: 'Page', icon: 'File' },
  { id: 'pay_at_table', label: 'Pay at table', icon: 'UtensilsCrossed' },
  { id: 'prepaid', label: 'Prépaiement (TCPOS)', icon: 'Wallet' },
  { id: 'system', label: 'Système', icon: 'Settings' },
];

const BLOC_CATEGORY_IDS: ReadonlySet<BlocCategory> = new Set(
  BLOC_CATEGORIES.map(entry => entry.id),
);

export function isBlocCategory(value: unknown): value is BlocCategory {
  return typeof value === 'string' && BLOC_CATEGORY_IDS.has(value as BlocCategory);
}
